/*eslint-disable no-unused-vars */
import { useRef, useState, useEffect, useMemo, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { frameColumnsForDocumentList } from '../../../utils/common';
import APIEndpoints from '../../../api/APIEndpoints'; //getDocumentUploadList
import { axiosInstance } from '../../../../api/axios';
import MasterPermissions from 'src/Admin/api/MasterPermissions';
//Third party packages
import debounce from 'lodash.debounce';
import useDate from '../../../utils/useDate';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  setSearchUserText,
  setRecordsPerPage,
  setPageNumber,
  resetUploadDocuments,
} from 'src/redux/admin/uploadDocumentSlice';

const useViewAllDocuments = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { searchUserText, recordsPerPage, pageNumber } = useSelector(
    (state) => state.uploadDocumentAdmin,
  );
  //The below one is Master List
  const [getDocumentData, setGetDocumentData] = useState([]);
  //FilteredList
  const [filteredRecords, setFilteredRecords] = useState([]);
  const [totalfilteredRecords, setTotalFilteredRecords] = useState([]);

  //pagination specific states
  const [pageSize, setPageSize] = useState(recordsPerPage);
  const [currentPage, setCurrentPage] = useState(pageNumber);
  const [totalRecords, setTotalRecords] = useState(0);
  //search
  const [searchText, setSearchText] = useState(searchUserText);
  const [search, setSearch] = useState(searchUserText);
  //API
  const [isAPIFailed, setIsAPIFailed] = useState(false);
  const [isNotFound, setIsNotFound] = useState(false);
  const [dataFetching, setDataFetching] = useState(false);
  const [loadingApiConfig, setLoadingApiConfig] = useState(true);

  const displayPaginationMininumCount = 10;
  const { formatDate } = useDate();

  const { userData } = useSelector((state) => state.user);

  const { selectedMember, memberDetails } = useSelector(
    (state) => state.memberModule,
  );

  const pageInfo = useRef(null);

  useEffect(() => {
    setTimeout(() => {
      if (pageInfo && pageInfo.current) {
        pageInfo.current.focus();
      }
    }, 10);
  }, []);

  const getParams = () => {
    const entityType = selectedMember?.mType?.toUpperCase();
    const memberID = memberDetails?.Member?.MemberID;
    const selectedMemberID = selectedMember?.ID;
    const memberSequenceNumber =
      entityType === 'EMPLOYEE'
        ? 0
        : memberDetails?.Member?.DependentsInfo?.filter(
            (item) => item?.OID === selectedMemberID,
          )[0]?.MemberSeqID;
    return `/EntityType/${entityType}/EntityKey/${memberID},${memberSequenceNumber}`;
  };

  const documentDataList = async (resetPage = '') => {
    setDataFetching(true);
    setLoadingApiConfig(true);
    const sortData = '/SortBy/sentDate/SortDirection/desc';
    const currentPageNumberLocal = resetPage ? 1 : pageNumber;
    const recordsPerPageLocal = resetPage ? 10 : recordsPerPage;
    const searchTextLocal = resetPage ? '' : searchUserText;
    try {
      const responseDocument = await axiosInstance.get(
        APIEndpoints.getDocumentUploadList() + getParams(),
      );
      const data = responseDocument.data.Response.listOfDocumentUploadObj;
      const InternalPermission = MasterPermissions.CheckPermission(
        'View Internal Document',
      );
      const ExpternalPermission = MasterPermissions.CheckPermission(
        'View External Document',
      );
      //const InternalPermission = true;
      //const ExpternalPermission = true;
      const permissionFilteredData =
        InternalPermission && ExpternalPermission
          ? data
          : InternalPermission
          ? data.filter((item) => item.Source === 'JMP')
          : ExpternalPermission
          ? data.filter((item) => item.Source === 'JAVELINA')
          : [];
      const totalRecords = permissionFilteredData.length;
      setGetDocumentData(permissionFilteredData);
      setTotalRecords(totalRecords);
      frameDataToDisplay(permissionFilteredData);
      //setCurrentPage(currentPageNumber);
      //setFilteredRecords(documentListData);

      setDataFetching(false);
      setIsNotFound(false);
      setIsAPIFailed(false);
      setLoadingApiConfig(false);
    } catch (error) {
      setDataFetching(false);
      setIsAPIFailed(true);
      setLoadingApiConfig(false);
      if (error.toString().includes('404')) {
        setIsNotFound(true);
        setLoadingApiConfig(false);
      }
    }
  };

  const pageSizeHandler = (pageSizeInfo) => {
    setPageSize(pageSizeInfo.value);
    setCurrentPage(1);
    filterSearchRecords();
  };

  const paginationHandler = (page) => {
    setCurrentPage(page);
    if (page > 1) {
      setFilteredRecords(
        totalfilteredRecords.slice(
          (page - 1) * pageSize,
          totalfilteredRecords.length,
        ),
      );
    } else {
      filterSearchRecords();
    }
  };

  const debouncedCall = useCallback(
    debounce(
      (currentVal) => (
        setSearch(currentVal), dispatch(setSearchUserText(currentVal))
      ),
      800,
    ),
    [],
  );

  const onSearch = (event) => {
    dispatch(setSearchUserText(event.target.value));
    dispatch(setPageNumber(1));
    setCurrentPage(1);
    setSearchText(event.target.value);
    debouncedCall(event.target.value);
  };

  const filterSearchRecords = () => {
    const requestTitleFilteredData = getDocumentData?.filter((item) =>
      item?.Title?.toLowerCase()?.includes(searchText.toLowerCase()),
    );

    const requestTypeFilteredData = getDocumentData?.filter((item) =>
      item?.EntityType?.toLowerCase()?.includes(searchText.toLowerCase()),
    );

    const requestDateFilteredData = getDocumentData?.filter((item) =>
      item?.SentDate?.toLowerCase()?.includes(searchText.toLowerCase()),
    );

    const finalFilteredData = new Set(
      sortingDataHandler([
        ...requestTitleFilteredData,
        ...requestTypeFilteredData,
        ...requestDateFilteredData,
      ]),
    );
    setCurrentPage(1);
    setTotalRecords([...finalFilteredData].length);
    frameDataToDisplay([...finalFilteredData]);
  };

  const sortingDataHandler = (data) => {
    if (data?.length > 0) {
      const sortByTime = data?.slice()?.sort((a, b) => {
        return b?.date - a?.date;
      });
      const sortByPin = sortByTime
        ?.filter((p1) => p1.pin === true)
        .concat(sortByTime?.filter((p1) => p1.pin !== true));
      return sortByPin;
    } else {
      return [];
    }
  };

  const columnNames = useMemo(() => {
    return frameColumnsForDocumentList();
  }, [getDocumentData]);

  const DepOidInfo = memberDetails?.Member?.DependentsInfo;

  const getSendDocumentForFun = (memOid, depOid) => {
    let sendDocFor;
    if (depOid && depOid != '') {
      const filterOidData = DepOidInfo.filter((item) => item.OID === depOid);
      return (sendDocFor =
        filterOidData[0]?.FirstName + ' ' + filterOidData[0]?.LastName);
    } else if (memOid) {
      return (sendDocFor =
        memberDetails?.Member?.FirstName +
        ' ' +
        memberDetails?.Member?.LastName);
    }
  };

  const frameDataToDisplay = (docData) => {
    if (docData?.length > 0) {
      const records = [];
      const iterationCount = docData.length;
      for (let iIndex = 0; iIndex < iterationCount; iIndex++) {
        records.push({
          Title: docData[iIndex].Title,
          Type: docData[iIndex].EntityType,
          ReferenceNumber: docData[iIndex].ReferenceNumber,
          SentDate: formatDate(docData[iIndex].SentDate),
          documents: `${docData[iIndex].TotalDocument + ' Documents'}`,
          Comment: docData[iIndex].Comment,
          senddocumentfor: getSendDocumentForFun(
            docData[iIndex].MemberOid,
            docData[iIndex].DependentOid,
          ),
          oid: docData[iIndex].Oid,
        });
      }
      setFilteredRecords(records);
      setTotalFilteredRecords(records);
    } else {
      setFilteredRecords([]);
      setTotalFilteredRecords([]);
    }
  };

  const [firstRender, setFirstRender] = useState(true);

  useEffect(() => {
    if (firstRender !== true) {
      dispatch(resetUploadDocuments());
      dispatch(setPageNumber(1));
      dispatch(setRecordsPerPage(10));
      dispatch(setSearchUserText(''));
      setPageSize(10);
      setCurrentPage(1);
      setSearch('');
      setSearchText('');
      documentDataList('reset');
    }
    setFirstRender(false);
  }, [selectedMember]);

  useEffect(() => {
    if (
      (location?.state?.selfPage === undefined &&
        location?.state?.documentDetailBackButton === undefined) ||
      location?.state?.sendDocumentBackButton !== undefined
    ) {
      dispatch(resetUploadDocuments());
      dispatch(setPageNumber(1));
      dispatch(setRecordsPerPage(10));
      dispatch(setSearchUserText(''));
      setPageSize(10);
      setCurrentPage(1);
      setSearch('');
      setSearchText('');
      documentDataList('reset');
      location.state = {
        ...location.state,
        selfPage: true,
        sendDocumentBackButton: undefined,
      };
    } else {
      documentDataList();
    }
  }, [pageNumber, recordsPerPage]);

  useEffect(() => {
    getDocumentData && getDocumentData?.length > 0 && filterSearchRecords();
  }, [search]);

  return {
    pageInfo,
    filteredRecords,
    pageSize,
    currentPage,
    totalRecords,
    searchText,
    columnNames,
    displayPaginationMininumCount,
    isNotFound,
    loadingApiConfig,
    isAPIFailed,
    dataFetching,
    setFilteredRecords,
    setPageSize,
    setCurrentPage,
    setTotalRecords,
    setSearchText,
    pageSizeHandler,
    paginationHandler,
    onSearch,
  };
};

export default useViewAllDocuments;
